import { PageContainer } from '@/components/page/PageContainer';
import { Button } from '@/components/shared/button/Button';
import { MonthPicker } from '@/components/shared/date/MonthPicker';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useIsOptimiserRunning } from '@/services/optimiser/useOptimiserJobs';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { useDaysInMonth } from '@/services/settings/useDates';
import { useMyLocation } from '@/services/useLocations';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { FiBarChart2, FiCalendar } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { NavPage } from '../../../components/page/Breadcrumbs';
import { PageHeader } from '../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../components/page/PageSizer';
import { EditSettingsProvider } from '../../../services/settings/legacy/EditSettingsService';
import { OptimisationHistoryModal } from '../optimise/OptimisationHistoryModal';
import { RosterOptionsProvider } from '../providers/RosterOptionsService';
import strings from '../roster.translations';
import { RosterCalendar } from './RosterCalendar';

export const RosterCalendarPage: FC = () => {
  const { date } = useParams<'date'>();

  const breadcrumbs: NavPage[] = [
    {
      name: 'Rosters',
      to: date ? `/rosters/calendar/${date}` : '/rosters'
    }
  ];

  const selectedMonth = date ? new Date(date) : new Date();
  const daysInMonth = useDaysInMonth(selectedMonth);
  const { id: locationId } = useMyLocation();
  const showOptimisationEvaluation = useSystemSettings(state => state.settings.optimiserShowEvaluation);
  const [historyOpen, setHistoryOpen] = useState(false);

  const navigate = useNavigate();

  const onChangeMonth = (newMonth: Date) => {
    navigate(`/rosters/calendar/${dateToApiFormat(newMonth)}`);
  };

  const isOptimiserRunning = useIsOptimiserRunning(selectedMonth);

  const isPastMonth = format(selectedMonth, 'yyyy-MM') < format(new Date(), 'yyyy-MM');

  return (
    <EditSettingsProvider>
      <RosterOptionsProvider date={selectedMonth}>
        <PageContainer
          customHeader={
            <PageHeader pageTitle="Rosters" icon={<FiCalendar />} breadcrumbs={breadcrumbs}>
              <div className="flex items-center gap-2">
                {!isPastMonth && (
                  <Button
                    onClick={() => navigate('/rosters/optimise', { state: { selectedMonth } })}
                    primary
                    loading={isOptimiserRunning}
                  >
                    {strings.optimiseRosters.title}
                  </Button>
                )}
                {showOptimisationEvaluation && (
                  <Button
                    primary
                    tint
                    className="border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                    onClick={() => setHistoryOpen(true)}
                    title={strings.optimiseRosters.evaluation.historyButtonLabel}
                  >
                    <FiBarChart2 className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </PageHeader>
          }
        >
          <div className="border-b bg-white">
            <PageSizer className="py-3">
              <MonthPicker value={selectedMonth} onChange={onChangeMonth} />
            </PageSizer>
          </div>
          <PageSizer className="" mode={SizerMode.PAGE}>
            <RosterCalendar date={selectedMonth} />
          </PageSizer>
          {showOptimisationEvaluation && (
            <OptimisationHistoryModal
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
              locationId={locationId}
              startDate={daysInMonth[0]}
              endDate={daysInMonth[daysInMonth.length - 1]}
            />
          )}
        </PageContainer>
      </RosterOptionsProvider>
    </EditSettingsProvider>
  );
};
